import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Mail } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const OtpBoxes = ({ value, onChange, disabled }) => {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { ref.current?.focus(); }, []);
  const digits = value.padEnd(6, " ").split("");
  return (
    <div className="tc-otp" onClick={() => ref.current?.focus()} data-testid="otp-boxes">
      <input ref={ref} className="tc-otp__input" value={value} inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} data-testid="otp-input" aria-label="6-digit confirmation code"
        style={{ position: "absolute", opacity: 0.01, width: 480, height: 80, left: "50%", transform: "translateX(-50%)", cursor: "text" }} />
      {[0, 1].map((g) => (
        <div key={g} style={{ display: "contents" }}>
          {g === 1 && <span className="tc-otp__dash" />}
          <div className="tc-otp__group">
            {[0, 1, 2].map((i) => { const idx = g * 3 + i; return <div key={idx} className={`tc-otp__box${focused && idx === Math.min(value.length, 5) ? " is-active" : ""}`} data-testid={`otp-box-${idx}`}>{digits[idx].trim()}</div>; })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const invited = sp.get("invited") === "1";
  const { login, user } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState(sp.get("email") || "");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [delivered, setDelivered] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => { if (user) navigate("/client", { replace: true }); }, [user, navigate]);
  useEffect(() => { if (!resendIn) return undefined; const t = setTimeout(() => setResendIn((s) => s - 1), 1000); return () => clearTimeout(t); }, [resendIn]);

  const request = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/request-code", { email: email.trim() });
      setDevCode(data.dev_code || null);
      setDelivered(data.delivered !== false);
      setStep("code"); setCode(""); setResendIn(30);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (c) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/verify", { email: email.trim(), code: c });
      await login(data.access_token, data.user);
      navigate("/client", { replace: true });
    } catch (err) { setError(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };
  useEffect(() => { if (code.length === 6 && !busy) verify(code); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tc-login" data-testid="login-page">
      <div className="tc-login__logo"><span className="tc-login__mark">TC</span><span className="tc-login__wordmark">Total Chat</span></div>
      {step === "email" ? (
        <>
          <h1>{invited ? "You're invited to Total Quality Insurance" : "Sign in to Total Quality Insurance"}</h1>
          <p className="tc-login__sub">{invited ? <>Sign in with <b>the email address your invitation was sent to</b> and we'll email you a one-time code.</> : <>Use the <b>email address you were invited with.</b> New members must be invited by a workspace admin.</>}</p>
          <form className="tc-login__form" onSubmit={request}>
            <input className="tc-input" type="email" placeholder="name@work-email.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required data-testid="login-email-input" />
            <button className="tc-btn tc-btn--aubergine lg" type="submit" disabled={busy || !email.trim()} data-testid="login-submit-button">{busy ? "Sending code…" : "Sign In With Email"}</button>
            {error && <div className="tc-login__error" data-testid="login-error"><AlertCircle size={14} /> {error}</div>}
            <div className="tc-login__note"><Mail size={16} /> We'll email you a 6-digit code for a password-free sign in.</div>
          </form>
        </>
      ) : (
        <>
          <h1>Check your email for a code</h1>
          <p className="tc-login__sub">We've sent a 6-digit code to <b>{email}</b>. The code expires shortly, so please enter it soon.</p>
          <OtpBoxes value={code} onChange={setCode} disabled={busy} />
          {error && <div className="tc-login__error" style={{ marginTop: 12 }} data-testid="login-error"><AlertCircle size={14} /> {error}</div>}
          {busy && <div className="tc-login__note">Verifying…</div>}
          {devCode && (
            <div className="tc-login__devcode" data-testid="dev-code-hint">
              <span>Development mode{delivered ? "" : " (email not delivered)"} — your code is</span><code data-testid="dev-code-value">{devCode}</code>
              <button className="tc-btn tc-btn--outline sm" onClick={() => setCode(devCode)} data-testid="dev-code-fill">Use code</button>
            </div>
          )}
          <div className="tc-login__note" style={{ marginTop: 24, gap: 16 }}>
            <button className="tc-login__resend" onClick={request} disabled={resendIn > 0 || busy} data-testid="login-resend-button">{resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}</button>
            <button className="tc-login__resend" onClick={() => { setStep("email"); setError(""); }} data-testid="login-change-email">Use a different email</button>
          </div>
          <p className="tc-login__note" style={{ marginTop: 20 }}>Can't find your code? Check your spam folder!</p>
        </>
      )}
      <div className="tc-login__footer"><a href="#privacy">Privacy & Terms</a><a href="#contact">Contact Us</a><span>Total Chat · Total Quality Insurance</span></div>
    </div>
  );
}
